import React from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Twitter, Heart } from 'lucide-react';
import { PortfolioConfig } from '../types/config';

interface FooterProps {
  config: PortfolioConfig;
}

const Footer: React.FC<FooterProps> = ({ config }) => {
  const socialLinks = [
    { icon: Github, href: config.contact.github, label: 'GitHub' },
    { icon: Linkedin, href: config.contact.linkedin, label: 'LinkedIn' },
    { icon: Twitter, href: config.contact.twitter, label: 'Twitter' },
  ];

  return (
    <footer className="bg-dark-900 dark:bg-black text-white py-12">
      <div className="container-custom px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-8 items-center">
          {/* Logo and Name */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center md:text-left"
          >
            <div className="flex items-center justify-center md:justify-start space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">VS</span>
              </div>
              <span className="text-xl font-bold gradient-text">
                {config.personal.name}
              </span>
            </div>
            <p className="text-dark-300 text-sm">
              {config.personal.title} at {config.personal.company}
            </p>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex justify-center space-x-6"
          >
            {socialLinks.map((social, index) => (
              <motion.a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-dark-800 rounded-lg flex items-center justify-center text-dark-300 hover:text-primary-400 hover:bg-dark-700 transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                viewport={{ once: true }}
              >
                <social.icon className="w-5 h-5" />
              </motion.a>
            ))}
          </motion.div>

          {/* Copyright */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center md:text-right"
          >
            <p className="text-dark-300 text-sm">
              © {new Date().getFullYear()} {config.personal.name}. All rights reserved.
            </p>
            <p className="text-dark-400 text-xs mt-1">
              Made with <Heart className="inline w-3 h-3 text-red-500" /> using React & Tailwind
            </p>
          </motion.div>
        </div>

        {/* Bottom Border */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-8 pt-8 border-t border-dark-800"
        >
          <div className="text-center">
            <p className="text-dark-400 text-xs">
              Built with modern web technologies and a passion for clean code.
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer; 